import React, { forwardRef } from 'react';
import type { ElementType } from 'react';
import { StyledBox } from './HBBox.styles';
import {
    buildBaseCss,
    buildSpacingCss,
    buildSizingCss,
    buildChildCss,
    buildDecorativeCss,
    pickBaseProps,
    pickSpacingProps,
    pickSizingProps,
    pickChildProps,
    pickDecorativeProps,
    responsiveValueCss,
    withDisplayName
} from '../../styles/layout/layout.utils';
import type { HBBoxProps } from './HBBox.types';
import type {
    StrictPolymorphicProps,
    PolymorphicRef
} from '../../styles/layout/layout.types';

type HBBoxPolymorphicProps<C extends ElementType = 'div'> =
    StrictPolymorphicProps<C, HBBoxProps>;

type HBBoxComponent = <C extends ElementType = 'div'>(
    props: HBBoxPolymorphicProps<C> & { ref?: PolymorphicRef<C> }
) => React.ReactElement | null;

function buildBoxCss(props: HBBoxProps) {
    return [
        ...buildBaseCss(props),
        ...buildSpacingCss(props),
        ...buildSizingCss(props),
        ...buildChildCss(props),
        ...buildDecorativeCss(props),
        responsiveValueCss(props.display, ['display'])
    ];
}

/**
 * Generic layout primitive for spacing, sizing, and display. Accepts padding, margin,
 * display, width, and height props mapped to the spacing token scale. Supports responsive
 * breakpoint objects and polymorphic rendering via the `as` prop.
 */
const HBBox = forwardRef(
    <C extends ElementType = 'div'>(
        {
            as,
            children,
            display,
            displayName,
            ...allProps
        }: HBBoxPolymorphicProps<C>,
        ref: PolymorphicRef<C>
    ) => {
        const [baseProps, afterBase] = pickBaseProps(allProps);
        const [spacingProps, afterSpacing] = pickSpacingProps(afterBase);
        const [sizingProps, afterSizing] = pickSizingProps(afterSpacing);
        const [childProps, afterChild] = pickChildProps(afterSizing);
        const [decorativeProps, afterDecorative] =
            pickDecorativeProps(afterChild);
        // Defense in depth: `className` and `style` are omitted from HBBoxProps,
        // but styled-components still injects a runtime `className` prop when
        // this component is wrapped via `styled(HBBox)`, and a consumer could
        // still pass `style` via `as any`/JS, regardless of the declared type.
        // Strip both explicitly so neither has any visual effect. Internal code
        // that needs styled(HBBox) back should use withStyles(HBBox) (see
        // ../../styles/layout/withStyles.tsx), which re-applies className via
        // the DOM node's classList instead of the prop pipeline.
        const {
            className: _discardedClassName,
            style: _discardedStyle,
            ...rest
        } = afterDecorative as typeof afterDecorative & {
            className?: string;
            style?: React.CSSProperties;
        };
        const layoutCss = buildBoxCss({
            ...baseProps,
            ...spacingProps,
            ...sizingProps,
            ...childProps,
            ...decorativeProps,
            display
        });

        return withDisplayName(
            displayName,
            <StyledBox
                as={as || 'div'}
                ref={ref}
                $layoutCss={layoutCss}
                data-component-name={displayName}
                {...rest}
            >
                {children}
            </StyledBox>
        );
    }
) as HBBoxComponent;

(HBBox as React.FC).displayName = 'HBBox';

export default HBBox;
